/**
 * Portal role guards — Iteration 21C-C Phase 2.
 *
 * `InternalOnlyRoute`  — /admin/*      permits INTERNAL_ROLES only
 * `TenantRoute`        — /institution/* permits `institution_admin`
 *                        (must have `institution_id`; internals bypass)
 * `DemoRoute`          — /demo-portal/* always accessible in sandbox;
 *                        server-side guards keep demo data isolated
 *
 * The gates match the backend `INTERNAL_ROLES` set from
 * `backend/veralok/tenant.py` and the citizen-owner rules from
 * `backend/veralok/citizen_auth.py`. Any drift is a bug — the source
 * of truth is the backend; these guards are UX polish that prevent
 * an already-authenticated user from wandering into a portal that
 * would refuse every backend call anyway.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const INTERNAL_ROLES = new Set([
  "system_admin",
  "compliance_officer",
  "security_officer",
  "support_officer",
  "auditor",
  "identity_officer",
  "executive_viewer",
]);

const TENANT_ROLES = new Set(["institution_admin"]);

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

export function InternalOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!INTERNAL_ROLES.has(user.role)) {
    return (
      <ForbiddenScreen
        title="Admin Console — access denied"
        detail={`Role "${user.role}" is not permitted here. This surface is reserved for VeraLok internal operators (system_admin, compliance, security, support, auditor, identity, executive).`}
        goBack="/institution"
      />
    );
  }
  return children;
}

export function TenantRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  const isInternal = INTERNAL_ROLES.has(user.role);
  const isTenant = TENANT_ROLES.has(user.role) && !!user.institution_id;
  if (!isInternal && !isTenant) {
    return (
      <ForbiddenScreen
        title="Institution Portal — access denied"
        detail={`Role "${user.role}" is not a valid tenant identity. Institution admins must be bound to an institution_id to reach this portal.`}
        goBack="/admin"
      />
    );
  }
  return children;
}

export function DemoRoute({ children }) {
  // M3 Demo-isolation defense-in-depth.
  //
  // The backend `/api/runtime/mode` returns `demo_portal_allowed=true`
  // in two situations:
  //   (a) `mode="sandbox"` + `mock_flag=true` — the legacy Demo Portal
  //       running against a sandbox backend (2026-01 behavior).
  //   (b) `mode="demo"` — the Demo v2 tenancy (2026-02-21+): a
  //       physically-isolated ECS task serving `demo.veralok.com`
  //       against the `veralok_demo` database with distinct secrets.
  // This guard accepts either state and refuses everything else.
  const [status, setStatus] = React.useState("checking"); // checking | allowed | refused | unreachable
  const [detail, setDetail] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${base}/api/runtime/mode`, { credentials: "include" });
        if (!res.ok) throw new Error(`runtime_mode_http_${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        // Demo v2 tenancy is a first-class Demo runtime.
        // demo_portal_allowed is now server-authoritative for both
        // sandbox+mock and demo modes.
        if (body?.demo_portal_allowed || body?.mode === "demo") {
          setStatus("allowed");
        } else {
          setStatus("refused");
          setDetail(`Backend runtime mode is "${body?.mode ?? "unknown"}". The Demo Portal requires either a sandbox+mock backend or the isolated Demo v2 tenancy.`);
        }
      } catch (err) {
        if (cancelled) return;
        setStatus("unreachable");
        setDetail("Could not verify backend runtime mode. The Demo Portal is refusing to load rather than risk touching non-sandbox data.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === "checking") return <AuthLoading />;
  if (status !== "allowed") {
    return (
      <ForbiddenScreen
        title="Demo Portal — sandbox required"
        detail={detail || "The Demo Portal only runs against a sandbox backend."}
        goBack="/"
      />
    );
  }
  return children;
}

/**
 * DeveloperRoute — /developer/*
 *
 * Backend gates every mutation with `developer:read` / `developer:manage`
 * (see rbac.py):
 *   developer:read   → system_admin, institution_admin, compliance_officer
 *   developer:manage → system_admin, institution_admin
 * Frontend guard mirrors that: authenticated users with one of those roles
 * see the portal. Everyone else gets a friendly Forbidden screen with a
 * link back to their primary portal.
 */
const DEVELOPER_ROLES = new Set([
  "system_admin",
  "institution_admin",
  "compliance_officer",
]);

export function DeveloperRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!DEVELOPER_ROLES.has(user.role)) {
    return (
      <ForbiddenScreen
        title="Developer Portal — access denied"
        detail={`Role "${user.role}" is not permitted here. This surface is for institution admins, compliance officers, and VeraLok internal operators managing API integrations.`}
        goBack="/"
      />
    );
  }
  return children;
}

function ForbiddenScreen({ title, detail, goBack }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="portal-forbidden">
      <div className="max-w-md text-center space-y-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">403 · Forbidden</div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{detail}</p>
        <a href={goBack} data-testid="forbidden-back-link"
           className="inline-block text-sm underline underline-offset-4">
          Return to the appropriate portal →
        </a>
      </div>
    </div>
  );
}
